import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SurveyStatus } from '@dsr/shared-types';
import {
  useSurveys,
  useCreateSurvey,
  useDeleteSurvey,
  useSitesLookup,
} from '../api/queries/surveys';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { DateInput } from '../components/DateInput';
import { toast } from '../components/Toasts';
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  FormRow,
  Input,
  Select,
  Table,
  Textarea,
  type BadgeTone,
  type SelectOption,
} from '../components/ui';
import './SurveysPage.css';

const STATUS_LABEL: Record<SurveyStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
};

const STATUSES: SurveyStatus[] = ['draft', 'submitted', 'approved'];

// Survey status → shared Badge tone (colors match the old sv-badge.status-* set).
const STATUS_TONE: Record<SurveyStatus, BadgeTone> = {
  draft: 'neutral',
  submitted: 'warn',
  approved: 'success',
};

/** "05/29/2026" — short display; em dash for null. */
function fmtDate(epochMs: number | null | undefined): string {
  if (epochMs === null || epochMs === undefined || !Number.isFinite(epochMs)) return '—';
  const d = new Date(epochMs);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
}

function firstFieldError(err: unknown): string | null {
  if (err instanceof ApiError && err.status === 400) {
    const body = err.body as
      | { error?: string; details?: { fieldErrors?: Record<string, string[]> } }
      | null;
    const fe = body?.details?.fieldErrors;
    if (fe) {
      const first = Object.values(fe).find((a) => Array.isArray(a) && a.length > 0);
      if (first && Array.isArray(first)) return first[0]!;
    }
    if (body?.error) return body.error;
  }
  return null;
}

export function SurveysPage(): JSX.Element {
  const navigate = useNavigate();

  // 'all' is the no-filter sentinel (Radix Select forbids an empty value).
  const [siteFilter, setSiteFilter] = useState<number | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<SurveyStatus | 'all'>('all');

  const { data, isPending, error } = useSurveys({
    ...(siteFilter !== 'all' ? { site_id: siteFilter } : {}),
    ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
  });
  const sitesQuery = useSitesLookup();
  const createSurvey = useCreateSurvey();
  const deleteSurvey = useDeleteSurvey();

  const sites = sitesQuery.data?.sites ?? [];
  const siteName = (id: number): string =>
    sites.find((s) => s.id === id)?.name ?? `Site #${id}`;

  // ---- Create modal state ----
  const [showNew, setShowNew] = useState(false);
  const [newSiteId, setNewSiteId] = useState<number | ''>('');
  const [newSurveyorId, setNewSurveyorId] = useState('');
  const [newConductedAt, setNewConductedAt] = useState<number | null>(null);
  const [newSummary, setNewSummary] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  const resetNew = () => {
    setNewSiteId('');
    setNewSurveyorId('');
    setNewConductedAt(null);
    setNewSummary('');
    setCreateError(null);
  };

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    if (newSiteId === '') {
      setCreateError('Choose a site for this survey.');
      return;
    }
    const surveyorId = newSurveyorId.trim();
    if (surveyorId && !/^\d+$/.test(surveyorId)) {
      setCreateError('Surveyor user ID must be a number.');
      return;
    }
    try {
      const created = await createSurvey.mutateAsync({
        site_id: newSiteId,
        ...(surveyorId ? { surveyor_user_id: Number(surveyorId) } : {}),
        ...(newConductedAt !== null ? { conducted_at: newConductedAt } : {}),
        ...(newSummary.trim() ? { summary: newSummary.trim() } : {}),
      });
      toast.success('Survey created.');
      setShowNew(false);
      resetNew();
      navigate(`/surveys/${created.survey.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setCreateError("You don't have permission to create surveys.");
        return;
      }
      setCreateError(firstFieldError(err) ?? 'Could not create survey. Try again.');
    }
  };

  // ---- Delete confirm state ----
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);

  const onConfirmDelete = async () => {
    if (deleteTarget === null) return;
    try {
      await deleteSurvey.mutateAsync(deleteTarget);
      toast.success('Survey deleted.');
    } catch (err) {
      const msg =
        err instanceof ApiError && err.status === 403
          ? "You don't have permission to delete surveys."
          : 'Could not delete survey.';
      toast.danger(msg);
    } finally {
      setDeleteTarget(null);
    }
  };

  if (error) {
    const msg =
      error instanceof ApiError && error.status === 403
        ? "You don't have permission to view site surveys."
        : 'Failed to load surveys.';
    return <p className="sv-status sv-error">{msg}</p>;
  }

  const surveys = data?.surveys ?? [];

  const siteFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All sites' },
    ...sites.map((s) => ({ value: String(s.id), label: s.name })),
  ];
  const statusFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All statuses' },
    ...STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>Site Surveys</h1>
          <p className="page-sub">
            Physical-security assessments and vulnerability findings by site.
          </p>
        </div>
        {can('survey.write') && (
          <div className="page-toolbar">
            <Button variant="primary" onClick={() => setShowNew(true)}>
              New survey
            </Button>
          </div>
        )}
      </header>

      <div className="filters-bar">
        <div className="sv-filter">
          <Select
            value={siteFilter === 'all' ? 'all' : String(siteFilter)}
            onValueChange={(v) => setSiteFilter(v === 'all' ? 'all' : Number(v))}
            options={siteFilterOptions}
            aria-label="Filter by site"
          />
        </div>
        <div className="sv-filter">
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as SurveyStatus | 'all')}
            options={statusFilterOptions}
            aria-label="Filter by status"
          />
        </div>
      </div>

      {isPending ? (
        <p className="sv-status">Loading surveys…</p>
      ) : surveys.length === 0 ? (
        <div className="sv-empty">
          <p>No surveys match the current filters.</p>
          {can('survey.write') && (
            <p className="sv-empty-meta">Click "New survey" to start an assessment.</p>
          )}
        </div>
      ) : (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Site</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Surveyor</Table.HeaderCell>
              <Table.HeaderCell>Conducted</Table.HeaderCell>
              <Table.HeaderCell>Summary</Table.HeaderCell>
              {can('survey.write') && <Table.HeaderCell></Table.HeaderCell>}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {surveys.map((s) => (
              <Table.Row
                key={s.id}
                interactive
                onClick={() => navigate(`/surveys/${s.id}`)}
                tabIndex={0}
                role="link"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigate(`/surveys/${s.id}`);
                }}
              >
                <Table.Cell tone="strong">{siteName(s.site_id)}</Table.Cell>
                <Table.Cell>
                  <Badge tone={STATUS_TONE[s.status]}>{STATUS_LABEL[s.status]}</Badge>
                </Table.Cell>
                <Table.Cell tone={s.surveyor_name ? undefined : 'muted'}>
                  {s.surveyor_name ?? 'Unassigned'}
                </Table.Cell>
                <Table.Cell tone={s.conducted_at ? undefined : 'muted'}>
                  {fmtDate(s.conducted_at)}
                </Table.Cell>
                <Table.Cell tone="muted">
                  {s.summary
                    ? s.summary.length > 80
                      ? `${s.summary.slice(0, 80)}…`
                      : s.summary
                    : '—'}
                </Table.Cell>
                {can('survey.write') && (
                  <Table.Cell onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="danger"
                      onClick={() => setDeleteTarget(s.id)}
                      disabled={deleteSurvey.isPending}
                    >
                      delete
                    </Button>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {/* ---- Create survey modal ---- */}
      <Dialog
        title="New site survey"
        open={showNew}
        busy={createSurvey.isPending}
        onClose={() => {
          setShowNew(false);
          resetNew();
        }}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setShowNew(false);
                resetNew();
              }}
              disabled={createSurvey.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="sv-new-form"
              variant="primary"
              disabled={createSurvey.isPending || newSiteId === ''}
            >
              {createSurvey.isPending ? 'Saving…' : 'Create survey'}
            </Button>
          </>
        }
      >
        <form id="sv-new-form" className="sv-form" onSubmit={onCreate}>
          <FormField label="Site" required>
            <select
              className="form-input"
              value={newSiteId}
              onChange={(e) =>
                setNewSiteId(e.target.value === '' ? '' : Number(e.target.value))
              }
              disabled={createSurvey.isPending}
              required
            >
              <option value="">Choose a site…</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </FormField>
          <FormRow>
            <FormField label="Surveyor user ID">
              <Input
                type="text"
                value={newSurveyorId}
                onChange={(e) => setNewSurveyorId(e.target.value)}
                placeholder="optional"
                inputMode="numeric"
                pattern="\d*"
                disabled={createSurvey.isPending}
              />
            </FormField>
            <FormField label="Conducted on">
              <DateInput
                value={newConductedAt}
                onChange={setNewConductedAt}
                disabled={createSurvey.isPending}
              />
            </FormField>
          </FormRow>
          <FormField label="Summary">
            <Textarea
              className="sv-textarea"
              value={newSummary}
              onChange={(e) => setNewSummary(e.target.value)}
              rows={4}
              maxLength={20_000}
              placeholder="Scope, methodology, and headline observations…"
              disabled={createSurvey.isPending}
            />
          </FormField>
          {createError && (
            <p className="sv-error-banner" role="alert">
              {createError}
            </p>
          )}
        </form>
      </Dialog>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete survey"
        message="Delete this survey and its findings? This can't be undone from here."
        confirmLabel="Delete"
        danger
        busy={deleteSurvey.isPending}
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </section>
  );
}
